<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import Drawer from 'primevue/drawer'
import PanelMenu from 'primevue/panelmenu'
import ConfirmDialog from 'primevue/confirmdialog'

const { t } = useI18n()

useHead({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] })

const menu = ref(false)
const route = useRoute()

const addActiveClass = (items: any[], currentPath: string): any[] => {
  return items.map((item) => {
    const newItem = { ...item }
    if (newItem.url && newItem.url === currentPath) {
      newItem.class = 'router-link-active'
    }
    if (newItem.items && Array.isArray(newItem.items)) {
      newItem.items = addActiveClass(newItem.items, currentPath)
    }
    return newItem
  })
}

const baseMenuItems = computed(() => [
  {
    key: '0',
    label: t('Dashboard'),
    icon: 'pi pi-home',
    url: '/super-admin',
  },
  {
    key: '1',
    label: t('All Businesses'),
    icon: 'pi pi-briefcase',
    url: '/super-admin/businesses',
  },
  {
    key: '2',
    label: t('Usage Metrics'),
    icon: 'pi pi-chart-line',
    url: '/super-admin/businesses/metrics',
  },
  {
    key: '3',
    label: t('Add Business'),
    icon: 'pi pi-plus',
    url: '/super-admin/businesses/add',
  },
  {
    key: '4',
    label: t('Create User & Business'),
    icon: 'pi pi-user-plus',
    url: '/super-admin/users-businesses/add',
  },
  {
    key: '5',
    label: t('Users'),
    icon: 'pi pi-users',
    url: '/super-admin/users',
  },
])

const menuItems = computed(() => addActiveClass(baseMenuItems.value, route.path))

const expandedKeys = ref<Record<string, boolean>>({})

const updateExpandedKeys = () => {
  const path = route.path
  const newExpandedKeys: Record<string, boolean> = {}

  baseMenuItems.value.forEach((item: any) => {
    if (item.items && Array.isArray(item.items)) {
      const hasActiveChild = item.items.some((child: any) => child.url === path)
      if (hasActiveChild && item.key) {
        newExpandedKeys[item.key] = true
      }
    }
  })

  expandedKeys.value = newExpandedKeys
}

watch(() => route.path, () => {
  updateExpandedKeys()
})

function logOut() {
  navigateTo('/auth/logout')
}

const toggle = () => {
  menu.value = !menu.value
}
</script>

<template>
  <div>
    <Toast />
    <ConfirmDialog />

    <!-- Mobile Drawer -->
    <Drawer
      v-model:visible="menu"
      header=""
      :pt="{
        header: { class: 'py-2 px-3' },
        content: { class: 'p-0' },
      }"
    >
      <div class="flex h-full flex-col">
        <div class="h-15 flex items-center justify-center px-4">
          <h2 class="text-xl font-bold">
            {{ t('Super Admin') }}
          </h2>
        </div>
        <div class="flex-1 overflow-auto">
          <PanelMenu
            v-model:expanded-keys="expandedKeys"
            :model="menuItems"
            class="w-full"
          />
        </div>
      </div>
    </Drawer>

    <!-- Main Layout -->
    <div class="page-wrapper flex flex-col lg:flex-row">
      <!-- Desktop Sidebar -->
      <div
        id="sidebar"
        class="hidden min-w-[250px] shadow-xl lg:flex lg:flex-col"
      >
        <div class="p-4 border-b">
          <h1 class="text-xl font-bold">
            {{ t('Super Admin') }}
          </h1>
        </div>
        <div class="flex-1 overflow-auto p-2">
          <PanelMenu
            v-model:expanded-keys="expandedKeys"
            :model="menuItems"
            class="w-full"
          />
        </div>
      </div>

      <!-- Content Area -->
      <div
        id="content"
        class="min-h-screen grow overflow-x-auto"
      >
        <!-- Top Bar -->
        <div class="top-bar flex py-2 shadow-md shadow-surface-200">
          <div class="flex grow items-center">
            <a
              class="ml-2 block cursor-pointer lg:hidden"
              @click.prevent="toggle"
            >
              <i class="pi pi-bars" />
            </a>
            <h2 class="text-base my-0 pl-2">
              {{ t('Super Admin') }}
            </h2>
          </div>
          <div class="pr-2 flex gap-2">
            <NuxtLink to="/app/financial">
              <Button
                type="button"
                :label="t('Back to Panel')"
                size="small"
                outlined
                icon="pi pi-arrow-left"
              />
            </NuxtLink>
            <Button
              type="button"
              :label="t('Sign out')"
              size="small"
              outlined
              @click="logOut"
            />
          </div>
        </div>

        <!-- Main Content -->
        <div class="main-content">
          <div class="p-2 md:p-6">
            <slot />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
